import React, { useEffect, useState } from "react";
import api from "../utils/api";
import CustomerFormModal from "../components/CustomerFormModal";
import CustomerTable from "../components/CustomerTable";
import CustomerTransactionModal from "../components/CustomerTransactionModal";
import { toast } from "react-toastify";
import Papa from "papaparse";
import Pagination from "./PaginationComp";

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchCustomers = async () => {
    try {
      setLoading(true);

      const res = await api.get(
        `/customers?page=${page}&search=${encodeURIComponent(search)}&showDeleted=${showDeleted}`
      );

      setCustomers(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 Debounce Search
  useEffect(() => {
    const delay = setTimeout(() => {
      fetchCustomers();
    }, 300);

    return () => clearTimeout(delay);
  }, [page, search, showDeleted]);

  // 🔥 Export CSV
  const exportCSV = () => {
    if (!customers.length) {
      toast.error("No data to export");
      return;
    }

    const formatted = customers.map((c) => ({
      Name: c.name,
      Mobile: c.mobile,
      Email: c.email || "-",
      TotalSpent: c.totalSpent || 0,
      LastVisit: c.lastVisit
        ? new Date(c.lastVisit).toLocaleDateString()
        : "-",
      Loyalty: c.totalSpent >= 20000 ? "VIP" : "Regular",
    }));

    const csv = Papa.unparse(formatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "customers.csv";
    link.click();
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-white p-6 rounded-xl shadow mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Customer Management</h1>
          <p className="text-sm text-gray-500">
            Manage and analyze your customers
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={exportCSV}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Export CSV
          </button>

          <button
            onClick={() => {
              setEditCustomer(null);
              setShowModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            + Add Customer
          </button>
        </div>
      </div>

      {/* Search + Toggle */}
      <div className="bg-white p-4 rounded-xl shadow mb-6 flex justify-between items-center">
        <input
          type="text"
          placeholder="Search by name, mobile, email"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          className="border px-4 py-2 rounded-lg w-72 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />

        <button
          onClick={() => {
            setShowDeleted(!showDeleted);
            setPage(1);
          }}
          className="border px-4 py-2 rounded-lg hover:bg-gray-100"
        >
          {showDeleted ? "Show Active" : "Show Deleted"}
        </button>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow p-4">
        {loading ? (
          <div className="text-center py-10 text-gray-500">
            Loading customers...
          </div>
        ) : (
          <CustomerTable
            customers={customers}
            showDeleted={showDeleted}
            onEdit={(cust) => {
              setEditCustomer(cust);
              setShowModal(true);
            }}
            onRefresh={fetchCustomers}
            onViewTransactions={(cust) => {
              setSelectedCustomer(cust);
              setShowHistoryModal(true);
            }}
          />
        )}
      </div>

      {/* Pagination */}
      <div className="mt-6">
        <Pagination
          totalPages={totalPages}
          currentPage={page}
          onPageChange={setPage}
        />
      </div>

      {/* Modals */}
      {showModal && (
        <CustomerFormModal
          customer={editCustomer}
          onClose={() => setShowModal(false)}
          refresh={fetchCustomers}
        />
      )}

      {showHistoryModal && selectedCustomer && (
        <CustomerTransactionModal
          customer={selectedCustomer}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  );
};

export default CustomersPage;
